import type {
    DashboardV3UserActiveStatusFilter,
    DashboardV3UserLoginMethodFilter,
    DashboardV3UserRangeValue,
    DashboardV3UserTableColumn,
    DashboardV3TradingProductType,
    DashboardV3TradingRangeValue,
    DashboardV3TradingTableColumn,
    DashboardV3PerpetualDetailColumn,
    DashboardV3RetentionGranularity,
} from './services/dashboardV3.types';

export type OverviewRangeValue = 'today' | 'yesterday' | '7d' | '30d' | 'custom' | 'all';

export const OVERVIEW_RANGE_LABELS = [
    { labelKey: 'module.dashboardV3.range.today', label: '今日', value: 'today' },
    { labelKey: 'module.dashboardV3.range.yesterday', label: '昨日', value: 'yesterday' },
    { labelKey: 'module.dashboardV3.range.sevenDays', label: '近7天', value: '7d' },
    { labelKey: 'module.dashboardV3.range.thirtyDays', label: '近30天', value: '30d' },
    { labelKey: 'module.dashboardV3.range.custom', label: '自定义', value: 'custom' },
    { labelKey: 'module.dashboardV3.range.all', label: '全部', value: 'all' },
] as const;

export const OVERVIEW_PRODUCT_TABS = [
    { labelKey: 'module.dashboardV3.overview.productTabs.overview', label: '总览', value: 'overview' },
    { labelKey: 'module.dashboardV3.overview.productTabs.perps', label: '永续合约', value: 'perps' },
    { labelKey: 'module.dashboardV3.overview.productTabs.predictSpot', label: '预测现货', value: 'predictSpot' },
    { labelKey: 'module.dashboardV3.overview.productTabs.predictLeveraged', label: '预测杠杆', value: 'predictLeveraged' },
    { labelKey: 'module.dashboardV3.overview.productTabs.predictGrid', label: '预测网格', value: 'predictGrid' },
] as const;

export const OVERVIEW_KPI_CARDS = [
    {
        key: 'registeredUsers',
        title: '今日注册用户',
        value: '2,150',
        delta: '+18.3%',
        deltaNote: 'vs 昨日',
        subtitle: '期间内新建账户去重数',
        variant: 'success',
        trend: 'up',
    },
    {
        key: 'depositUsers',
        title: '今日入金用户',
        value: '1,284',
        delta: '+12.4%',
        deltaNote: 'vs 昨日',
        subtitle: '期间内有入金行为的去重用户',
        variant: 'primary',
        trend: 'up',
    },
    {
        key: 'tradingUsers',
        title: '今日交易用户',
        value: '892',
        delta: '+8.2%',
        deltaNote: 'vs 昨日',
        subtitle: '期间内有交易行为的去重用户',
        variant: 'secondary',
        trend: 'up',
    },
    {
        key: 'depositToTrade',
        title: '入金 → 交易 转化率',
        value: '69.5%',
        delta: '+2.1%',
        deltaNote: 'vs 昨日',
        subtitle: '同期入金用户中有交易的比例',
        variant: 'info',
        trend: 'up',
    },
    {
        key: 'd1Retention',
        title: 'D+1 留存率',
        value: '15.0%',
        delta: '-1.2%',
        deltaNote: 'vs 昨日',
        subtitle: '期间新交易用户次日复归率',
        variant: 'warning',
        trend: 'down',
    },
] as const;

export const OVERVIEW_FINANCE_CARDS = [
    {
        key: 'platformTvl',
        title: '平台 TVL',
        value: '$85,200,000',
        delta: '+3.2%',
        deltaNote: 'vs 7日前',
        subtitle: '实时快照 · 不受时间筛选影响',
        variant: 'primary',
        trend: 'up',
    },
    {
        key: 'depositVolume',
        title: '今日充值',
        value: '$2,348,200',
        delta: '+9.6%',
        deltaNote: 'vs 昨日',
        subtitle: '用户链上充值入平台总额',
        variant: 'success',
        trend: 'up',
    },
    {
        key: 'withdrawVolume',
        title: '今日提现',
        value: '$1,240,600',
        delta: '+4.1%',
        deltaNote: 'vs 昨日',
        subtitle: '用户链上提现出平台总额',
        variant: 'error',
        trend: 'up',
    },
] as const;

export const OVERVIEW_RETENTION_MATRIX = [
    {
        label: '本期',
        d1: { value: '15.0%', delta: '-1.2%', trend: 'down' },
        weekly: { value: '38.2%', delta: '+2.4%', trend: 'up' },
        monthly: { value: '52.4%', delta: '+3.3%', trend: 'up' },
        sample: '892',
    },
    {
        label: '上期',
        d1: { value: '16.2%' },
        weekly: { value: '35.8%' },
        monthly: { value: '49.1%' },
        sample: '834',
    },
] as const;

export const OVERVIEW_FUNNEL = [
    { key: 'registered', label: '新注册用户', value: 2150, color: '#81C995', rateText: '' },
    { key: 'deposit', label: '入金用户', value: 1284, color: '#BB86FC', rateText: '↓ 注册→入金 59.7%' },
    { key: 'trading', label: '交易用户', value: 892, color: '#03DAC6', rateText: '↓ 入金→交易 69.5%' },
    { key: 'retention', label: '次日留存', value: 134, color: '#64B5F6', rateText: '↓ D+1 留存 15%' },
] as const;

const trendDates = ['3/27', '3/28', '3/29', '3/30', '3/31', '4/1', '4/2', '4/3', '4/4', '4/5', '4/6', '4/7', '4/8', '4/9'];

export const OVERVIEW_TREND_CARDS = [
    {
        key: 'perps',
        title: '永续合约交易趋势',
        barColor: '#C9963D',
        data: trendDates.map((date, index) => ({
            date,
            volume: [4200000, 4600000, 3900000, 5100000, 4800000, 3600000, 4100000, 4900000, 5050000, 4700000, 5300000, 5050000, 4920000, 5250000][index],
            users: [520, 570, 470, 610, 590, 430, 480, 550, 575, 540, 620, 600, 580, 650][index],
        })),
    },
    {
        key: 'predictSpot',
        title: '预测现货交易趋势',
        barColor: '#26A99A',
        data: trendDates.map((date, index) => ({
            date,
            volume: [1420000, 1540000, 1320000, 1700000, 1600000, 1250000, 1380000, 1620000, 1660000, 1510000, 1740000, 1680000, 1640000, 1760000][index],
            users: [185, 205, 172, 225, 210, 164, 180, 208, 214, 198, 230, 220, 212, 160][index],
        })),
    },
    {
        key: 'predictLeveraged',
        title: '预测杠杆交易趋势',
        barColor: '#9575CD',
        data: trendDates.map((date, index) => ({
            date,
            volume: [720000, 780000, 680000, 870000, 820000, 610000, 700000, 840000, 860000, 800000, 900000, 860000, 830000, 880000][index],
            users: [92, 98, 84, 112, 108, 78, 86, 104, 110, 100, 116, 112, 106, 80][index],
        })),
    },
    {
        key: 'predictGrid',
        title: '预测网格交易趋势',
        barColor: '#6EA66F',
        data: trendDates.map((date, index) => ({
            date,
            volume: [210000, 235000, 185000, 278000, 250000, 168000, 198000, 255000, 262000, 230000, 285000, 270000, 258000, 286000][index],
            users: [30, 34, 28, 38, 36, 25, 30, 34, 36, 33, 39, 37, 36, 27][index],
        })),
    },
] as const;



// --------------------------邀请Tab-----------------------------------------------------

export type RangeValue = '7d' | '30d' | 'month' | 'custom' | 'all';
export type MethodFilter = 'all' | 'wallet' | 'email';
export type UserTypeFilter = 'all' | 'agent' | 'user';
export type SortOrder = 'asc' | 'desc';
export type ReferralProductType = 'perps' | 'prediction_spot' | 'prediction_leverage';

export interface ReferralQueryParams {
    range: RangeValue;
    startDate?: string;
    endDate?: string;
    productType?: ReferralProductType;
}

export interface ReferralUsersParams extends ReferralQueryParams {
    page: number;
    size: number;
    loginMethod?: MethodFilter;
    userType?: UserTypeFilter;
    sortBy?: string;
    sortOrder?: SortOrder;
}

export const RANGE_LABELS = [
    { labelKey: 'module.dashboardV3.range.sevenDays', value: '7d' },
    { labelKey: 'module.dashboardV3.range.thirtyDays', value: '30d' },
    { labelKey: 'module.dashboardV3.range.month', value: 'month' },
    { labelKey: 'module.dashboardV3.range.custom', value: 'custom' },
    { labelKey: 'module.dashboardV3.range.all', value: 'all' },
] as const;

export const RANGE_LABEL_MAP = {
    '7d': 'module.dashboardV3.range.sevenDays',
    '30d': 'module.dashboardV3.range.thirtyDays',
    month: 'module.dashboardV3.range.thisMonth',
    custom: 'module.dashboardV3.range.customDate',
    all: 'module.dashboardV3.range.all',
} as const;

export const METHOD_CHIPS = [
    { labelKey: 'module.dashboardV3.range.all', value: 'all' },
    { labelKey: 'module.dashboardV3.filters.wallet', value: 'wallet' },
    { labelKey: 'module.dashboardV3.filters.email', value: 'email' },
] as const;

export const USER_TYPE_CHIPS = [
    { labelKey: 'module.dashboardV3.range.all', value: 'all' },
    { labelKey: 'module.dashboardV3.filters.agent', value: 'agent' },
    { labelKey: 'module.dashboardV3.filters.user', value: 'user' },
] as const;

export const TAB_LABELS = [
    { labelKey: 'module.dashboardV3.tabs.overview', value: 'overview' },
    { labelKey: 'module.dashboardV3.tabs.users', value: 'users' },
    { labelKey: 'module.dashboardV3.tabs.deposit', value: 'deposit' },
    { labelKey: 'module.dashboardV3.tabs.trading', value: 'trading' },
    { labelKey: 'module.dashboardV3.tabs.retention', value: 'retention' },
    { labelKey: 'module.dashboardV3.tabs.referral', value: 'referral' },
] as const;

export const REFERRAL_PRODUCT_TABS = [
    { labelKey: 'module.dashboardV3.productTabs.perps', value: 'perps' },
    { labelKey: 'module.dashboardV3.productTabs.predictionSpot', value: 'prediction_spot' },
    { labelKey: 'module.dashboardV3.productTabs.predictionLeverage', value: 'prediction_leverage' },
] as const;

export type DashboardV3ReferralTableColumnKey =
    | 'walletAddr'
    | 'loginMethod'
    | 'loginAccount'
    | 'userId'
    | 'country'
    | 'code'
    | 'parentCode'
    | 'topCode'
    | 'utype'
    | 'directRefs'
    | 'teamRefs'
    | 'downlineVolume'
    | 'downlineDeposit'
    | 'totalFees'
    | 'rebateIncome';

export type DashboardV3ReferralTableColumn = {
    key: DashboardV3ReferralTableColumnKey;
    labelKey: string;
    align: 'left' | 'right';
    mono: boolean;
    sortable?: boolean;
};

export const TABLE_COLUMNS: readonly DashboardV3ReferralTableColumn[] = [
    { key: 'walletAddr', labelKey: 'module.dashboardV3.table.walletAddr', align: 'left', mono: false },
    { key: 'loginMethod', labelKey: 'module.dashboardV3.table.loginMethod', align: 'left', mono: false },
    { key: 'loginAccount', labelKey: 'module.dashboardV3.table.loginAccount', align: 'left', mono: false },
    { key: 'userId', labelKey: 'module.dashboardV3.table.userId', align: 'left', mono: false },
    { key: 'country', labelKey: 'module.dashboardV3.table.country', align: 'left', mono: false, sortable: false },
    { key: 'code', labelKey: 'module.dashboardV3.table.code', align: 'left', mono: false },
    { key: 'parentCode', labelKey: 'module.dashboardV3.table.parentCode', align: 'left', mono: false },
    { key: 'topCode', labelKey: 'module.dashboardV3.table.topCode', align: 'left', mono: false },
    { key: 'utype', labelKey: 'module.dashboardV3.table.utype', align: 'left', mono: false },
    { key: 'directRefs', labelKey: 'module.dashboardV3.table.directRefs', align: 'right', mono: false },
    { key: 'teamRefs', labelKey: 'module.dashboardV3.table.teamRefs', align: 'right', mono: false },
    { key: 'downlineVolume', labelKey: 'module.dashboardV3.table.downlineVolume', align: 'right', mono: false },
    { key: 'downlineDeposit', labelKey: 'module.dashboardV3.table.downlineDeposit', align: 'right', mono: false },
    { key: 'totalFees', labelKey: 'module.dashboardV3.table.totalFees', align: 'right', mono: false },
    { key: 'rebateIncome', labelKey: 'module.dashboardV3.table.rebateIncome', align: 'right', mono: false },
] as const;

export const CELL_COLOR: Partial<Record<DashboardV3ReferralTableColumnKey, string>> = {
    walletAddr: '#FFFFFF',
    downlineVolume: '#03DAC6',
    downlineDeposit: '#03DAC6',
    totalFees: '#64B5F6',
    rebateIncome: '#81C995',
};

export function formatUtc8Date(date: Date) {
    const utc8 = new Date(date.getTime() + 8 * 60 * 60 * 1000);
    return utc8.toISOString().slice(0, 10);
}

export function formatLocalDate(date: Date) {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');

    return `${year}-${month}-${day}`;
}

/**
 * 保留兼容旧代码。
 * 如果 formatDate 只用于初始化 today / default date，可以指向 UTC+8。
 */
export function formatDate(date: Date) {
    return formatUtc8Date(date);
}


// -------------------------- Users Tab -----------------------

export const DASHBOARD_V3_USER_RANGE_LABELS = [
    { labelKey: 'module.dashboardV3.range.today', label: 'Today', value: 'today' },
    { labelKey: 'module.dashboardV3.range.yesterday', label: 'Yesterday', value: 'yesterday' },
    { labelKey: 'module.dashboardV3.range.sevenDays', label: 'Last 7 Days', value: '7d' },
    { labelKey: 'module.dashboardV3.range.thirtyDays', label: 'Last 30 Days', value: '30d' },
    { labelKey: 'module.dashboardV3.range.threeMonths', label: 'Last 3 Months', value: '3m' },
    { labelKey: 'module.dashboardV3.range.custom', label: 'Custom', value: 'custom' },
    { labelKey: 'module.dashboardV3.range.all', label: 'All', value: 'all' },
] as const satisfies readonly {
    labelKey: string;
    label: string;
    value: DashboardV3UserRangeValue;
}[];

export const DASHBOARD_V3_USER_LOGIN_METHOD_CHIPS = [
    { labelKey: 'module.dashboardV3.range.all', label: 'All', value: 'all' },
    { labelKey: 'module.dashboardV3.filters.wallet', label: 'Wallet', value: 'wallet' },
    { labelKey: 'module.dashboardV3.filters.email', label: 'Email', value: 'email' },
] as const satisfies readonly {
    labelKey: string;
    label: string;
    value: DashboardV3UserLoginMethodFilter;
}[];

export const DASHBOARD_V3_USER_STATUS_CHIPS = [
    { labelKey: 'module.dashboardV3.range.all', label: 'All', value: 'all' },
    { labelKey: 'module.dashboardV3.users.filters.active', label: 'Active', value: 'active' },
    { labelKey: 'module.dashboardV3.users.filters.inactive', label: 'Inactive', value: 'inactive' },
] as const satisfies readonly {
    labelKey: string;
    label: string;
    value: DashboardV3UserActiveStatusFilter;
}[];

export const DASHBOARD_V3_USER_TABLE_COLUMNS: readonly DashboardV3UserTableColumn[] = [
    {
        key: 'userId',
        labelKey: 'module.dashboardV3.users.table.userId',
        label: 'UserID',
        minWidth: 130,
        mono: true,
        sortable: true,
    },
    {
        key: 'walletAddress',
        labelKey: 'module.dashboardV3.users.table.walletAddress',
        label: 'User Address',
        minWidth: 155,
        mono: true,
        sortable: true,
    },
    {
        key: 'loginAccount',
        labelKey: 'module.dashboardV3.users.table.loginAccount',
        label: 'Login Account',
        minWidth: 170,
        mono: true,
        sortable: true,
    },
    {
        key: 'loginMethod',
        labelKey: 'module.dashboardV3.users.table.loginMethod',
        label: 'Login Method',
        minWidth: 125,
        sortable: true,
    },
    {
        key: 'parentWalletAddress',
        labelKey: 'module.dashboardV3.users.table.parentWalletAddress',
        label: 'Parent Address',
        minWidth: 170,
        mono: true,
        sortable: true,
    },
    {
        key: 'country',
        labelKey: 'module.dashboardV3.users.table.country',
        label: 'Country',
        minWidth: 115,
        sortable: false,
    },
    {
        key: 'registeredAt',
        labelKey: 'module.dashboardV3.users.table.registeredAt',
        label: 'Registered At',
        minWidth: 135,
        sortable: true,
    },
    {
        key: 'lastActiveAt',
        labelKey: 'module.dashboardV3.users.table.lastActiveAt',
        label: 'Last Active',
        minWidth: 135,
        sortable: false,
    },
    {
        key: 'status',
        labelKey: 'module.dashboardV3.users.table.status',
        label: 'Status',
        minWidth: 105,
        sortable: false,
    },

    {
        key: 'perpetualVolume',
        labelKey: 'module.dashboardV3.users.table.perpetualVolume',
        label: 'Perpetual Volume',
        align: 'right',
        minWidth: 180,
        sortable: true,
        metric: true,
    },
    {
        key: 'perpetualPnl',
        labelKey: 'module.dashboardV3.users.table.perpetualPnl',
        label: 'Perpetual PnL',
        align: 'right',
        minWidth: 160,
        sortable: true,
        metric: true,
    },
    {
        key: 'perpetualDepositCount',
        labelKey: 'module.dashboardV3.users.table.perpetualDepositCount',
        label: 'Perpetual Deposit Count',
        align: 'right',
        minWidth: 220,
        sortable: true,
        metric: true,
    },
    {
        key: 'perpetualDepositAmount',
        labelKey: 'module.dashboardV3.users.table.perpetualDepositAmount',
        label: 'Perpetual Deposit Amount',
        align: 'right',
        minWidth: 230,
        sortable: true,
        metric: true,
    },

    {
        key: 'predictionSpotVolume',
        labelKey: 'module.dashboardV3.users.table.predictionSpotVolume',
        label: 'Prediction Spot Volume',
        align: 'right',
        minWidth: 210,
        sortable: true,
        metric: true,
    },
    {
        key: 'predictionSpotPnl',
        labelKey: 'module.dashboardV3.users.table.predictionSpotPnl',
        label: 'Prediction Spot PnL',
        align: 'right',
        minWidth: 190,
        sortable: true,
        metric: true,
    },
    {
        key: 'predictionSpotDepositCount',
        labelKey: 'module.dashboardV3.users.table.predictionSpotDepositCount',
        label: 'Prediction Spot Deposit Count',
        align: 'right',
        minWidth: 265,
        sortable: true,
        metric: true,
    },
    {
        key: 'predictionSpotDepositAmount',
        labelKey: 'module.dashboardV3.users.table.predictionSpotDepositAmount',
        label: 'Prediction Spot Deposit Amount',
        align: 'right',
        minWidth: 275,
        sortable: true,
        metric: true,
    },

    {
        key: 'predictionLeverageVolume',
        labelKey: 'module.dashboardV3.users.table.predictionLeverageVolume',
        label: 'Prediction Leverage Volume',
        align: 'right',
        minWidth: 240,
        sortable: true,
        metric: true,
    },
    {
        key: 'predictionLeveragePnl',
        labelKey: 'module.dashboardV3.users.table.predictionLeveragePnl',
        label: 'Prediction Leverage PnL',
        align: 'right',
        minWidth: 220,
        sortable: true,
        metric: true,
    },
    {
        key: 'predictionLeverageDepositCount',
        labelKey: 'module.dashboardV3.users.table.predictionLeverageDepositCount',
        label: 'Prediction Leverage Deposit Count',
        align: 'right',
        minWidth: 300,
        sortable: true,
        metric: true,
    },
    {
        key: 'predictionLeverageDepositAmount',
        labelKey: 'module.dashboardV3.users.table.predictionLeverageDepositAmount',
        label: 'Prediction Leverage Deposit Amount',
        align: 'right',
        minWidth: 315,
        sortable: true,
        metric: true,
    },

    {
        key: 'predictionGridVolume',
        labelKey: 'module.dashboardV3.users.table.predictionGridVolume',
        label: 'Prediction Grid Volume',
        align: 'right',
        minWidth: 215,
        sortable: true,
        metric: true,
    },
    {
        key: 'predictionGridPnl',
        labelKey: 'module.dashboardV3.users.table.predictionGridPnl',
        label: 'Prediction Grid PnL',
        align: 'right',
        minWidth: 190,
        sortable: true,
        metric: true,
    },
    {
        key: 'predictionGridDepositCount',
        labelKey: 'module.dashboardV3.users.table.predictionGridDepositCount',
        label: 'Prediction Grid Deposit Count',
        align: 'right',
        minWidth: 270,
        sortable: true,
        metric: true,
    },
    {
        key: 'predictionGridDepositAmount',
        labelKey: 'module.dashboardV3.users.table.predictionGridDepositAmount',
        label: 'Prediction Grid Deposit Amount',
        align: 'right',
        minWidth: 280,
        sortable: true,
        metric: true,
    },
] as const;


// -------------------------- Trading Tab -----------------------

export const DASHBOARD_V3_TRADING_RANGE_LABELS = [
    { labelKey: 'module.dashboardV3.range.today', label: 'Today', value: 'today' },
    { labelKey: 'module.dashboardV3.range.yesterday', label: 'Yesterday', value: 'yesterday' },
    { labelKey: 'module.dashboardV3.range.sevenDays', label: 'Last 7 Days', value: '7d' },
    { labelKey: 'module.dashboardV3.range.thirtyDays', label: 'Last 30 Days', value: '30d' },
    { labelKey: 'module.dashboardV3.range.custom', label: 'Custom', value: 'custom' },
    { labelKey: 'module.dashboardV3.range.all', label: 'All', value: 'all' },
] as const satisfies readonly {
    labelKey: string;
    label: string;
    value: DashboardV3TradingRangeValue;
}[];

export const DASHBOARD_V3_TRADING_PRODUCT_TABS = [
    { labelKey: 'module.dashboardV3.productTabs.perps', label: 'Perpetuals', value: 'perps' },
    {
        labelKey: 'module.dashboardV3.productTabs.predictionSpot',
        label: 'Prediction Spot',
        value: 'prediction_spot',
    },
    {
        labelKey: 'module.dashboardV3.productTabs.predictionLeverage',
        label: 'Prediction Leverage',
        value: 'prediction_leverage',
    },
    {
        labelKey: 'module.dashboardV3.productTabs.predictionGrid',
        label: 'Prediction Grid',
        value: 'prediction_grid',
    },
] as const satisfies readonly {
    labelKey: string;
    label: string;
    value: DashboardV3TradingProductType;
}[];

// 每个交易产品对应的强调色（与子 tab 下划线、卡片走的一套配色一致）
export const DASHBOARD_V3_TRADING_PRODUCT_COLORS: Record<DashboardV3TradingProductType, string> = {
    perps: '#FFB74D',
    prediction_spot: '#03DAC6',
    prediction_leverage: '#BB86FC',
    prediction_grid: '#81C995',
};

export const DASHBOARD_V3_TRADING_TABLE_COLUMNS: readonly DashboardV3TradingTableColumn[] = [
    {
        key: 'walletAddress',
        labelKey: 'module.dashboardV3.trading.table.walletAddress',
        label: 'User Address',
        minWidth: 220,
        mono: true,
        align: 'left',
    },
    {
        key: 'loginMethod',
        labelKey: 'module.dashboardV3.trading.table.loginMethod',
        label: 'Login Method',
        minWidth: 150,
        align: 'left',
    },
    {
        key: 'tradingVolume',
        labelKey: 'module.dashboardV3.trading.table.tradingVolume',
        label: 'Trading Volume (USDC)',
        minWidth: 190,
        align: 'right',
    },
    {
        key: 'firstTrade',
        labelKey: 'module.dashboardV3.trading.table.firstTrade',
        label: 'First Trade',
        minWidth: 130,
        align: 'left',
    },
    {
        key: 'tradedAt',
        labelKey: 'module.dashboardV3.trading.table.tradedAt',
        label: 'Time',
        minWidth: 190,
        align: 'left',
    },
] as const;

export const DASHBOARD_V3_PREDICTION_SPOT_TRADE_COLUMNS: readonly DashboardV3TradingTableColumn[] = [
    {
        key: 'userId',
        labelKey: 'module.dashboardV3.trading.table.userId',
        label: 'User ID',
        minWidth: 140,
        mono: true,
        align: 'left',
        sortable: false,
    },
    {
        key: 'maker',
        labelKey: 'module.predictionTrading.maker',
        label: 'Maker',
        minWidth: 180,
        mono: true,
        align: 'left',
        sortable: false,
    },
    {
        key: 'firstTrade',
        labelKey: 'module.dashboardV3.trading.table.firstTrade',
        label: '首次交易',
        minWidth: 110,
        align: 'center',
        sortable: false,
    },
    {
        key: 'side',
        labelKey: 'module.predictionTrading.side',
        label: 'Side',
        minWidth: 90,
        align: 'center',
        sortable: false,
    },
    {
        key: 'outcome',
        labelKey: 'module.predictionTrading.outcome',
        label: 'Outcome',
        minWidth: 120,
        align: 'left',
        sortable: false,
    },
    {
        key: 'size',
        labelKey: 'module.predictionTrading.size',
        label: 'Size',
        minWidth: 120,
        align: 'right',
        sortable: false,
    },
    {
        key: 'price',
        labelKey: 'module.predictionTrading.price',
        label: 'Price',
        minWidth: 120,
        align: 'right',
        sortable: false,
    },
    {
        key: 'premiumVolume',
        labelKey: 'module.dashboardV3.trading.table.premiumVolume',
        label: 'Premium Volume (USDC)',
        minWidth: 170,
        align: 'right',
        sortable: false,
    },
    {
        key: 'notionalVolume',
        labelKey: 'module.dashboardV3.trading.table.notionalVolume',
        label: 'Notional Volume (USDC)',
        minWidth: 170,
        align: 'right',
        sortable: false,
    },
    {
        key: 'feeUsdc',
        labelKey: 'module.predictionTrading.feeUsdc',
        label: 'Fee (USDC)',
        minWidth: 130,
        align: 'right',
        sortable: false,
    },
    {
        key: 'builderFee',
        labelKey: 'module.predictionTrading.builderFee',
        label: 'Builder Fee',
        minWidth: 130,
        align: 'right',
        sortable: false,
    },
    {
        key: 'status',
        labelKey: 'module.predictionTrading.status',
        label: 'Status',
        minWidth: 120,
        align: 'center',
        sortable: false,
    },
    {
        key: 'matchTime',
        labelKey: 'module.predictionTrading.matchTime',
        label: 'Match Time',
        minWidth: 180,
        align: 'left',
        sortable: false,
    },
    {
        key: 'market',
        labelKey: 'module.predictionTrading.market',
        label: 'Market',
        minWidth: 180,
        align: 'left',
        sortable: false,
    },
    {
        key: 'transactionHash',
        labelKey: 'module.predictionTrading.transactionHash',
        label: 'Transaction Hash',
        minWidth: 160,
        align: 'left',
        sortable: false,
    },
] as const;

export const DASHBOARD_V3_PREDICTION_LEVERAGE_TRADE_COLUMNS: readonly DashboardV3TradingTableColumn[] = [
    {
        key: 'createdAt',
        labelKey: 'module.dashboardV3.trading.table.createdAt',
        label: 'Created Time',
        minWidth: 180,
        align: 'left',
    },
    {
        key: 'userId',
        labelKey: 'module.dashboardV3.trading.table.userId',
        label: 'User ID',
        minWidth: 170,
        mono: true,
        align: 'left',
    },
    {
        key: 'walletAddress',
        labelKey: 'module.dashboardV3.trading.table.walletAddress',
        label: 'Wallet Address',
        minWidth: 170,
        mono: true,
        align: 'left',
    },
    {
        key: 'marketName',
        labelKey: 'module.dashboardV3.trading.table.market',
        label: 'Market',
        minWidth: 260,
        align: 'left',
    },
    {
        key: 'marketId',
        labelKey: 'module.dashboardV3.trading.table.marketId',
        label: 'Market ID',
        minWidth: 150,
        mono: true,
        align: 'left',
    },
    {
        key: 'outcome',
        labelKey: 'module.dashboardV3.trading.table.outcome',
        label: 'Outcome',
        minWidth: 110,
        align: 'center',
    },
    {
        key: 'status',
        labelKey: 'module.predictionTrading.status',
        label: 'Status',
        minWidth: 110,
        align: 'center',
    },
    {
        key: 'isFirstTrade',
        labelKey: 'module.dashboardV3.trading.table.firstTrade',
        label: 'First Trade',
        minWidth: 120,
        align: 'center',
    },
    {
        key: 'actionType',
        labelKey: 'module.dashboardV3.trading.table.actionType',
        label: 'Action Type',
        minWidth: 130,
        align: 'center',
    },
    {
        key: 'side',
        labelKey: 'module.predictionTrading.side',
        label: 'Side',
        minWidth: 90,
        align: 'center',
    },
    {
        key: 'leverage',
        labelKey: 'module.dashboardV3.trading.table.leverage',
        label: 'Leverage',
        minWidth: 110,
        align: 'right',
    },
    {
        key: 'shares',
        labelKey: 'module.dashboardV3.trading.table.shares',
        label: 'Shares',
        minWidth: 130,
        align: 'right',
    },
    {
        key: 'premiumVolume',
        labelKey: 'module.dashboardV3.trading.table.premiumVolume',
        label: 'Premium Volume',
        minWidth: 160,
        align: 'right',
    },
    {
        key: 'notionalVolume',
        labelKey: 'module.dashboardV3.trading.table.notionalVolume',
        label: 'Notional Volume',
        minWidth: 160,
        align: 'right',
    },
    {
        key: 'xbitFeeCents',
        labelKey: 'module.dashboardV3.trading.table.tradingFee',
        label: 'Trading Fee',
        minWidth: 130,
        align: 'right',
    },
    {
        key: 'realizedPnlCents',
        labelKey: 'module.dashboardV3.trading.table.realizedPnl',
        label: 'Realized PnL',
        minWidth: 140,
        align: 'right',
    },
    {
        key: 'liquidationFeeCents',
        labelKey: 'module.dashboardV3.trading.table.liquidationFee',
        label: 'Liquidation Fee',
        minWidth: 150,
        align: 'right',
    },
    {
        key: 'marginCents',
        labelKey: 'module.dashboardV3.trading.table.margin',
        label: 'Margin',
        minWidth: 120,
        align: 'right',
    },
    {
        key: 'fundingFeeCents',
        labelKey: 'module.dashboardV3.trading.table.fundingFee',
        label: 'Funding Fee',
        minWidth: 130,
        align: 'right',
    },
    {
        key: 'totalFeeCents',
        labelKey: 'module.dashboardV3.trading.table.totalFee',
        label: 'Total Fee',
        minWidth: 130,
        align: 'right',
    },
    {
        key: 'orderId',
        labelKey: 'module.dashboardV3.trading.table.orderId',
        label: 'Order ID',
        minWidth: 120,
        mono: true,
        align: 'left',
    },
    {
        key: 'positionId',
        labelKey: 'module.dashboardV3.trading.table.positionId',
        label: 'Position ID',
        minWidth: 120,
        mono: true,
        align: 'left',
    },
    {
        key: 'lastAttemptError',
        labelKey: 'module.dashboardV3.trading.table.lastAttemptError',
        label: 'Last Error',
        minWidth: 180,
        align: 'left',
    },
    {
        key: 'resolutionNote',
        labelKey: 'module.dashboardV3.trading.table.resolutionNote',
        label: 'Resolution Note',
        minWidth: 180,
        align: 'left',
    },
    {
        key: 'settledAt',
        labelKey: 'module.dashboardV3.trading.table.settledAt',
        label: 'Settled Time',
        minWidth: 180,
        align: 'left',
    },
] as const;

export const DASHBOARD_V3_PREDICTION_GRID_TRADE_COLUMNS: readonly DashboardV3TradingTableColumn[] = [
    {
        key: 'id',
        labelKey: 'module.dashboardV3.trading.table.id',
        label: 'ID',
        minWidth: 140,
        mono: true,
        align: 'left',
        sortable: false,
    },
    {
        key: 'agencyUid',
        labelKey: 'module.dashboardV3.trading.table.agencyUid',
        label: 'Agency UID',
        minWidth: 190,
        mono: true,
        align: 'left',
        sortable: false,
    },
    {
        key: 'userName',
        labelKey: 'module.dashboardV3.trading.table.userName',
        label: 'User Name',
        minWidth: 190,
        align: 'left',
        sortable: false,
    },
    {
        key: 'userId',
        labelKey: 'module.dashboardV3.trading.table.userId',
        label: 'User ID',
        minWidth: 190,
        mono: true,
        align: 'left',
        sortable: false,
    },
    {
        key: 'pairId',
        labelKey: 'module.dashboardV3.trading.table.pairId',
        label: 'Pair ID',
        minWidth: 150,
        mono: true,
        align: 'left',
        sortable: false,
    },
    {
        key: 'currency',
        labelKey: 'module.dashboardV3.trading.table.currency',
        label: 'Currency',
        minWidth: 110,
        align: 'left',
        sortable: false,
    },
    {
        key: 'amount',
        labelKey: 'module.dashboardV3.trading.table.amount',
        label: 'Amount',
        minWidth: 120,
        align: 'right',
        sortable: false,
    },
    {
        key: 'coefficient',
        labelKey: 'module.dashboardV3.trading.table.coefficient',
        label: 'Coefficient',
        minWidth: 120,
        align: 'right',
        sortable: false,
    },
    {
        key: 'status',
        labelKey: 'module.predictionTrading.status',
        label: 'Status',
        minWidth: 120,
        align: 'center',
        sortable: false,
    },
    {
        key: 'charged',
        labelKey: 'module.dashboardV3.trading.table.charged',
        label: 'Charged',
        minWidth: 110,
        align: 'center',
        sortable: false,
    },
    {
        key: 'chargedTxId',
        labelKey: 'module.dashboardV3.trading.table.chargedTxId',
        label: 'Charged Tx ID',
        minWidth: 180,
        mono: true,
        align: 'left',
        sortable: false,
    },
    {
        key: 'win',
        labelKey: 'module.dashboardV3.trading.table.win',
        label: 'Win',
        minWidth: 90,
        align: 'center',
        sortable: false,
    },
    {
        key: 'rewarded',
        labelKey: 'module.dashboardV3.trading.table.rewarded',
        label: 'Rewarded',
        minWidth: 110,
        align: 'center',
        sortable: false,
    },
    {
        key: 'payout',
        labelKey: 'module.dashboardV3.trading.table.payout',
        label: 'Payout',
        minWidth: 120,
        align: 'right',
        sortable: false,
    },
    {
        key: 'rewardTxId',
        labelKey: 'module.dashboardV3.trading.table.rewardTxId',
        label: 'Reward Tx ID',
        minWidth: 180,
        mono: true,
        align: 'left',
        sortable: false,
    },
    {
        key: 'cancelled',
        labelKey: 'module.dashboardV3.trading.table.cancelled',
        label: 'Cancelled',
        minWidth: 110,
        align: 'center',
        sortable: false,
    },
    {
        key: 'cancelledAt',
        labelKey: 'module.dashboardV3.trading.table.cancelledAt',
        label: 'Cancelled At',
        minWidth: 170,
        align: 'left',
        sortable: false,
    },
    {
        key: 'refundRate',
        labelKey: 'module.dashboardV3.trading.table.refundRate',
        label: 'Refund Rate',
        minWidth: 120,
        align: 'right',
        sortable: false,
    },
    {
        key: 'refundAmount',
        labelKey: 'module.dashboardV3.trading.table.refundAmount',
        label: 'Refund Amount',
        minWidth: 140,
        align: 'right',
        sortable: false,
    },
    {
        key: 'demo',
        labelKey: 'module.dashboardV3.trading.table.demo',
        label: 'Demo',
        minWidth: 90,
        align: 'center',
        sortable: false,
    },
    {
        key: 'slippage',
        labelKey: 'module.dashboardV3.trading.table.slippage',
        label: 'Slippage',
        minWidth: 120,
        align: 'right',
        sortable: false,
    },
    {
        key: 'createdAt',
        labelKey: 'module.dashboardV3.trading.table.createdAt',
        label: 'Created Time',
        minWidth: 170,
        align: 'left',
        sortable: false,
    },
    {
        key: 'updatedAt',
        labelKey: 'module.dashboardV3.trading.table.updatedAt',
        label: 'Updated Time',
        minWidth: 170,
        align: 'left',
        sortable: false,
    },
] as const;

// 合约 tab 的 Trading Detail（永续逐笔成交）列：以 userId 为首列
export const DASHBOARD_V3_PERPETUAL_DETAIL_COLUMNS: readonly DashboardV3PerpetualDetailColumn[] = [
    {
        key: 'userId',
        labelKey: 'module.dashboardV3.trading.table.userId',
        label: 'User ID',
        minWidth: 140,
        align: 'left',
    },
    {
        key: 'symbol',
        labelKey: 'module.perpetualTrading.symbol',
        label: 'Symbol',
        minWidth: 120,
        align: 'left',
    },
    {
        key: 'coin',
        labelKey: 'module.perpetualTrading.coin',
        label: 'Coin',
        minWidth: 90,
        align: 'left',
    },
    {
        key: 'side',
        labelKey: 'module.perpetualTrading.side',
        label: 'Side',
        minWidth: 90,
        align: 'center',
    },
    {
        key: 'price',
        labelKey: 'module.perpetualTrading.price',
        label: 'Price',
        minWidth: 120,
        align: 'right',
        sortField: 'avgPrice',
    },
    {
        key: 'size',
        labelKey: 'module.perpetualTrading.size',
        label: 'Size',
        minWidth: 120,
        align: 'right',
    },
    {
        key: 'volume',
        labelKey: 'module.perpetualTrading.volume',
        label: 'Volume',
        minWidth: 120,
        align: 'right',
    },
    {
        key: 'buildFee',
        labelKey: 'module.perpetualTrading.buildFee',
        label: 'Build Fee',
        minWidth: 110,
        align: 'right',
    },
    {
        key: 'status',
        labelKey: 'module.perpetualTrading.status',
        label: 'Status',
        minWidth: 100,
        align: 'center',
    },
    {
        key: 'firstTrade',
        labelKey: 'module.dashboardV3.trading.table.firstTrade',
        label: '首次交易',
        minWidth: 110,
        align: 'center',
    },
    {
        key: 'orderTimeId',
        labelKey: 'module.perpetualTrading.orderTimeId',
        label: 'Order Time ID',
        minWidth: 200,
        align: 'left',
        sortField: 'createdAtRaw',
    },
    {
        key: 'walletAddress',
        labelKey: 'module.perpetualTrading.walletAddress',
        label: 'Wallet Address',
        minWidth: 200,
        align: 'left',
    },
    {
        key: 'hash',
        labelKey: 'module.dashboardV3.trading.table.hash',
        label: 'Hash',
        minWidth: 120,
        align: 'left',
    },
] as const;

// -------------------------- Retention Tab --------------------------

/** 右上角粒度切换，只影响留存趋势图和队列留存矩阵，不影响 KPI 卡片。 */
export const DASHBOARD_V3_RETENTION_GRANULARITY_TABS = [
    { labelKey: 'module.dashboardV3.retention.granularity.day', label: 'Day', value: 'day' },
    { labelKey: 'module.dashboardV3.retention.granularity.week', label: 'Week', value: 'week' },
    { labelKey: 'module.dashboardV3.retention.granularity.month', label: 'Month', value: 'month' },
] as const;

/**
 * 各粒度覆盖规则，与后端约定一致：
 *   日 → 最近 14 个队列，D+1 ~ D+7
 *   周 → 最近 12 个队列，W+1 ~ W+8
 *   月 → 最近 12 个队列，M+1 ~ M+12
 * periodPrefix 用于矩阵列头和趋势图标题。
 */
export const DASHBOARD_V3_RETENTION_GRANULARITY_META: Record<
    DashboardV3RetentionGranularity,
    { periodPrefix: 'D' | 'W' | 'M'; maxPeriod: number; cohorts: number }
> = {
    day: { periodPrefix: 'D', maxPeriod: 7, cohorts: 14 },
    week: { periodPrefix: 'W', maxPeriod: 8, cohorts: 12 },
    month: { periodPrefix: 'M', maxPeriod: 12, cohorts: 12 },
};

/** 队列矩阵单元格配色阈值（%），good 及以上绿色，medium~good 橙色，medium 以下红色。 */
export const DASHBOARD_V3_RETENTION_RATE_THRESHOLDS: Record<
    DashboardV3RetentionGranularity,
    { good: number; medium: number }
> = {
    day: { good: 20, medium: 12 },
    week: { good: 35, medium: 20 },
    month: { good: 50, medium: 30 },
};

export const DASHBOARD_V3_RETENTION_RATE_COLORS = {
    good: { text: '#81C995', bg: 'rgba(129,201,149,0.14)' },
    medium: { text: '#FFB74D', bg: 'rgba(255,183,77,0.14)' },
    poor: { text: '#CF6679', bg: 'rgba(207,102,121,0.14)' },
} as const;

/** KPI 三张卡的顶部色条，与原型一致。 */
export const DASHBOARD_V3_RETENTION_KPI_ACCENTS: Record<string, string> = {
    daily: '#81C995',
    weekly: '#BB86FC',
    monthly: '#64B5F6',
};
